// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { User, Message, Conversation, CompanionRequest } from '../types';
import { setUserId, clearUserId } from '../utils/ga4';
import {
  sendConnectionRequest,
  getIncomingRequests,
  getOutgoingRequests,
  acceptConnection,
  rejectConnection,
  withdrawRequest,
  getMyTribe,
  getAllMyConnections,
  type TribeConnection,
  type IncomingRequest,
  type OutgoingRequest,
  type MyTribeMember,
  type ConnectionStatus,
} from '../services/tribeService';

/** Extra required fields we now collect at Sign Up */
export type RegisterExtras = {
  fullName: string;
  gender: 'male' | 'female' | 'non-binary' | 'prefer-not-to-say';
  dateOfBirth: string; // ISO yyyy-mm-dd
  phone: string;       // stored in profiles.mobile
};

const PREVERIFY_LS_KEY = 'preverify_expires_at';
const PREVERIFY_DURATION_MS = 60 * 60 * 1000; // 1 hour
const getPreviewExpiresAt = () => Number(localStorage.getItem(PREVERIFY_LS_KEY) || '0');
const setPreviewForOneHour = () =>
  localStorage.setItem(PREVERIFY_LS_KEY, String(Date.now() + PREVERIFY_DURATION_MS));
const endPreviewNow = () => localStorage.removeItem(PREVERIFY_LS_KEY);

const computePreviewSecondsLeft = () =>
  Math.max(0, Math.floor((getPreviewExpiresAt() - Date.now()) / 1000));

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;
  sessionChecked: boolean;
  isLoading: boolean;
  accessToken: string | null;

  // NEW: preview + verification helpers for UI
  previewActive: boolean;
  previewSecondsLeft: number;
  endPreview: () => void;
  resendVerification: () => Promise<void>;

  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  loginWithMagicLink: (email: string) => Promise<{ success: boolean; message: string }>;
  register: (
    email: string,
    password: string,
    username: string,
    extras: RegisterExtras
  ) => Promise<{ success: boolean; needsEmailConfirmation: boolean }>;

  logout: () => Promise<void>;
  updateUser: (userData: User) => Promise<boolean>;
  refreshCurrentUser: () => Promise<boolean>;
  setAdminUser: (user: User) => void;

  // Location services
  requestAndStoreCurrentLocation: () => Promise<boolean>;

  // Messaging
  sendMessage: (receiverId: string, content: string) => Promise<Message | null>;
  getMessagesWith: (userId: string) => Promise<Message[]>;
  getConversations: () => Promise<Conversation[]>;
  markAllMessagesAsRead: (senderId: string) => Promise<boolean>;

  // Tribe Connections
  sendTribeConnectionRequest: (addresseeId: string, message?: string) => Promise<TribeConnection>;
  acceptTribeConnection: (connectionId: string) => Promise<TribeConnection>;
  rejectTribeConnection: (connectionId: string) => Promise<TribeConnection>;
  withdrawTribeConnectionRequest: (connectionId: string) => Promise<TribeConnection>;
  getIncomingTribeRequests: () => IncomingRequest[];
  getOutgoingTribeRequests: () => OutgoingRequest[];
  getMyTribeMembers: () => MyTribeMember[];
  getTribeConnectionStatus: (otherUserId: string) => ConnectionStatus | 'none';
  refreshTribeData: () => Promise<void>;

  // Companion requests
  sendCompanionRequest: (receiverId: string) => Promise<CompanionRequest | null>;
  respondToCompanionRequest: (requestId: string, response: 'accepted' | 'rejected') => Promise<boolean>;
  getCompanionRequests: () => CompanionRequest[];
  isCompanion: (userId: string) => boolean;

  // Favorites
  addToFavorites: (userId: string) => Promise<boolean>;
  removeFromFavorites: (userId: string) => Promise<boolean>;
  getFavorites: () => User[];
  hasAddedToFavorites: (userId: string) => boolean;
  isMutualFavorite: (userId: string) => boolean;

  // Subscription and verification
  isVerified: () => boolean;
  isVerificationPending: () => boolean;
  hasVeriffBadge: () => boolean;
  getSubscriptionDetails: () => {
    planName: string;
    status: string;
    nextBillingDate?: string;
  };
  cancelSubscription: (userId: string, immediate: boolean) => Promise<boolean>;

  // User blocking
  blockUser: (userId: string) => Promise<boolean>;
  unblockUser: (userId: string) => Promise<boolean>;
  isUserBlocked: (userId: string) => boolean;

  // Connection checking
  checkIfCanConnect: (userId: string, currentUserId: string) => Promise<boolean>;

  // VIP Launch Access
  vipActive: boolean;
  vipEndsAt: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Tiny safety net: prevent long hangs on network hiccups
const withTimeout = <T,>(p: Promise<T>, ms = 7000) =>
  Promise.race([
    p,
    new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms)),
  ]);

// Helper to check if VIP window is active
const isVipActive = (u?: { vipLaunch?: boolean; trialEndsAt?: string | null }) => {
  if (!u?.vipLaunch || !u.trialEndsAt) return false;
  return Date.now() < new Date(u.trialEndsAt).getTime();
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // NEW: preview + countdown derived state
  const [previewActive, setPreviewActive] = useState(false);
  const [previewSecondsLeft, setPreviewSecondsLeft] = useState<number>(0);

  // Companion / favorites / blocking (lightweight stubs)
  const [companionRequests, setCompanionRequests] = useState<CompanionRequest[]>([]);
  const [favorites, setFavorites] = useState<User[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);

  // Tribe Connections State
  const [incomingTribeRequests, setIncomingTribeRequests] = useState<IncomingRequest[]>([]);
  const [outgoingTribeRequests, setOutgoingTribeRequests] = useState<OutgoingRequest[]>([]);
  const [myTribeMembers, setMyTribeMembers] = useState<MyTribeMember[]>([]);
  const [allTribeConnections, setAllTribeConnections] = useState<TribeConnection[]>([]);

  // Track ongoing profile loads to prevent duplicate fetches
  const loadingProfilesRef = useRef<Map<string, Promise<User | null>>>(new Map());
  const registrationCooldownRef = useRef<number>(0);

  /** Load full profile & map to User. Return null if not found/error (caller may fallback). */
  const loadUserProfile = async (userId: string): Promise<User | null> => {
    // Check if we're already loading this profile
    const existingLoad = loadingProfilesRef.current.get(userId);
    if (existingLoad) {
      console.log('AuthProvider: Profile already loading for:', userId, '- reusing existing promise');
      return existingLoad;
    }

    // Create new load promise
    const loadPromise = (async () => {
      try {
        console.log('AuthProvider: Loading user profile for:', userId);

      // Get current session for email fallback
      const { data: { session } } = await supabase.auth.getSession();
      const sessionEmail = session?.user?.email ?? '';

      // Fetch current profile (if any)
      const { data: profile, error } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
        7000
      );

      let p = profile;

      // If no profile yet, re-try once (trigger may be milliseconds behind)
      if (!p && !error) {
        await new Promise(r => setTimeout(r, 150));
        const { data: p2, error: e2 } = await withTimeout(
          supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
          7000
        );
        if (e2) {
          console.error('AuthProvider: Database error loading profile (retry):', e2);
          return null;
        }
        p = p2 || null;
      } else if (error) {
        console.error('AuthProvider: Database error loading profile:', error);
        return null;
      }

      if (!p) {
        console.error('AuthProvider: No profile data available after normalization');
        return null;
      }

      // Map DB row -> app User
      const user: User = {
        id: p.id,
        username: p.username ?? '',
        email: p.email ?? sessionEmail,
        fullName: p.full_name ?? null,
        bio: p.bio ?? 'Tell me about yourself',
        interests: p.interests ?? [],
        location: p.location ?? null,
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        gender: p.gender ?? 'prefer-not-to-say',
        dateOfBirth: p.date_of_birth ?? null,
        emailVerified: !!p.email_verified,
        isVerified: !!p.is_verified,
        verificationStatus: p.verification_status ?? 'unverified',
        subscriptionTier: p.subscription_tier ?? 'free',
        subscriptionExpiresAt: p.subscription_expires_at ?? null,
        subscriptionCancelled: !!p.subscription_cancelled,
        subscriptionCancellationDate: p.subscription_cancellation_date ?? null,
        phone: p.mobile ?? null,
        avatarUrl: p.avatar_url ?? null,
        rating: p.rating ?? null,
        ratingCount: p.rating_count ?? 0,
        profileVisibility: p.profile_visibility ?? 'public',
        profileStatus: p.profile_status ?? 'registered',
        createdAt: p.created_at,
        favorites: p.favorites ?? [],

        // VIP Launch Access fields
        vipLaunch: !!p.vip_launch,
        trialStartsAt: p.trial_starts_at ?? null,
        trialEndsAt: p.trial_ends_at ?? null,

        // Admin access
        isAdmin: !!p.is_admin,

        // Profile completion
        profileComplete: !!p.profile_complete,

        // Tribe carousel visibility
        showInTribeCarousel: p.show_in_tribe_carousel ?? true,
      };

      console.debug('[Auth] profile normalized', {
        profileStatus: user.profileStatus,
        username: user.username,
        isVerified: user.isVerified,
        subscriptionTier: user.subscriptionTier,
        vipLaunch: user.vipLaunch,
        profileComplete: user.profileComplete,
        trialEndsAt: user.trialEndsAt,
      });

      setCurrentUser(user);

      // best-effort aux loads (don't block UI)
      loadFavorites(userId).catch(console.error);
      loadBlockedUsers(userId).catch(console.error);
      refreshTribeData().catch(console.error);

        return user;
      } catch (error) {
        console.error('AuthProvider: Error in loadUserProfile:', error);
        return null;
      } finally {
        // Clean up from loading map
        loadingProfilesRef.current.delete(userId);
      }
    })();

    // Store the promise for deduplication
    loadingProfilesRef.current.set(userId, loadPromise);

    return loadPromise;
  };

  // Soft-loads (safe no-ops for now)
  const loadCompanionRequests = async (_userId: string) => {
    try {
      setCompanionRequests([]);
    } catch (error) {
      console.error('Error loading companion requests:', error);
    }
  };
  const loadFavorites = async (_userId: string) => {
    try {
      setFavorites([]);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };
  const loadBlockedUsers = async (_userId: string) => {
    try {
      setBlockedUsers([]);
    } catch (error) {
      console.error('Error loading blocked users:', error);
    }
  };

  // ---- Tribe refresh (fixed & throttled) ----
  const tribeRefreshInFlight = React.useRef(false);
  const lastTribeRefreshAt = React.useRef(0);
  const TRIBE_REFRESH_MIN_INTERVAL_MS = 1500;

  const refreshTribeData = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) return;

    if (tribeRefreshInFlight.current) return;
    const now = Date.now();
    if (now - lastTribeRefreshAt.current < TRIBE_REFRESH_MIN_INTERVAL_MS) return;

    tribeRefreshInFlight.current = true;
    lastTribeRefreshAt.current = now;

    console.time('[tribe] refresh');
    try {
      const [incoming, outgoing, myTribe, allEdges] = await Promise.all([
        getIncomingRequests(),
        getOutgoingRequests(),
        getMyTribe(),
        getAllMyConnections(),
      ]);

      setIncomingTribeRequests(incoming);
      setOutgoingTribeRequests(outgoing);
      setMyTribeMembers(myTribe);
      setAllTribeConnections(allEdges);
    } catch (error) {
      console.error('Error refreshing tribe data:', error);
    } finally {
      tribeRefreshInFlight.current = false;
      console.timeEnd('[tribe] refresh');
    }
  }, []);

  /** Unified session handler. Never throws. Fallback to minimal user if profile fails. */
  const handleSession = useCallback(async (session: any | null) => {
    try {
      if (!session) {
        console.log('[Auth] No session');
        setCurrentUser(null);
        setIsAuthenticated(false);
        setAccessToken(null);

        // clear tribe state
        setIncomingTribeRequests([]);
        setOutgoingTribeRequests([]);
        setMyTribeMembers([]);
        setAllTribeConnections([]);
        return;
      }
      const authUser = session.user;
      console.log('[Auth] Session user:', authUser?.id);
      setAccessToken(session.access_token ?? null);

      const loaded = await loadUserProfile(authUser.id);
      if (!loaded) {
        console.warn('[Auth] Using minimal user fallback');
        setCurrentUser({
          id: authUser.id,
          email: authUser.email ?? '',
          username:
            authUser.user_metadata?.username ||
            authUser.email?.split('@')[0] ||
            'User',
          emailVerified: false,
          isVerified: false,
          verificationStatus: 'unverified',
          subscriptionTier: 'free',
          interests: [],
          favorites: [],
          profileVisibility: 'public',
          createdAt: new Date().toISOString(),
          // Default VIP fields for minimal user
          vipLaunch: false,
          trialStartsAt: null,
          trialEndsAt: null,
        } as User);
      }

      setIsAuthenticated(true);
    } catch (e) {
      console.error('[Auth] handleSession error:', e);
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Initialization + subscription. Always flips flags in finally. */
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      console.log('AuthProvider: Initializing auth state');
      setIsLoading(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        console.log('[Auth] getSession ->', session ? 'found session' : 'no session');
        await handleSession(session);
      } catch (e) {
        console.error('[Auth] initializeAuth error:', e);
      } finally {
        if (!mounted) return;
        setSessionChecked(true);
        setHasHydrated(true);
        setIsLoading(false);
        console.log(
          '[Auth] Flags set -> sessionChecked:true, hasHydrated:true, isLoading:false'
        );
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('[Auth] onAuthStateChange:', _event);
      handleSession(session);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [handleSession]);

  // 🔄 cross-tab + focus sync so the original tab picks up sessions created in another tab
  useEffect(() => {
    const sync = async () => {
      if (Date.now() < registrationCooldownRef.current) {
        console.log('[Auth] Skipping sync - registration cooldown active');
        return;
      }
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[Auth] getSession error during sync (ignoring):', error.message);
          return;
        }
        handleSession(data.session ?? null);
      } catch (e) {
        console.warn('[Auth] Sync error (ignoring):', e);
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.includes('auth-token')) sync();
    };
    const onFocus = () => {
      if (document.visibilityState === 'visible') sync();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [handleSession]);

  // NEW: Maintain previewActive + countdown from localStorage and user verification
  useEffect(() => {
    const tick = () => setPreviewSecondsLeft(computePreviewSecondsLeft());
    tick(); // initial compute
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // Admin never needs preview
    if (currentUser?.email === 'admin@vybetribe.app') {
      setPreviewActive(false);
      return;
    }
    const expiresAt = getPreviewExpiresAt();
    // Preview is active if:
    // 1. User is logged in (has session)
    // 2. AND within the preview window (Date.now() < expiresAt)
    // 3. AND user is not verified
    const withinWindow = Date.now() < expiresAt;
    const needsVerification = currentUser && !currentUser.isVerified;
    const active = withinWindow && needsVerification;
    console.log('[AuthContext] Preview computation:', {
      expiresAt,
      now: Date.now(),
      withinWindow,
      needsVerification,
      active,
      currentUser: currentUser?.id,
    });
    setPreviewActive(active);
  }, [currentUser?.isVerified, currentUser?.email, currentUser?.id]);

  // NEW: Cross-browser unlock — poll auth user every 15s until verified
  useEffect(() => {
    // Admin doesn't need polling
    if (currentUser?.email === 'admin@vybetribe.app') return;
    if (currentUser?.isVerified) return;
    const id = setInterval(async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          console.warn('[Auth] Poll getUser error (ignoring):', error.message);
          return;
        }
        const confirmed =
          !!data.user?.email_confirmed_at || !!data.user?.user_metadata?.email_verified;
        if (confirmed) {
          // refresh profile (assumes your backend/trigger sets profiles.is_verified shortly after)
          await refreshCurrentUser();
          // Only end preview if profile is complete (has interests)
          if (currentUser?.interests && currentUser.interests.length > 0) {
            endPreviewNow();
          }
        }
      } catch (err) {
        console.warn('[Auth] Poll getUser exception (ignoring):', err);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [currentUser?.isVerified, currentUser?.email, currentUser?.interests]);

  // Auto-refresh when user returns to tab (especially after completing verification)
  useEffect(() => {
    if (!currentUser) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() < registrationCooldownRef.current) {
          console.log('[Auth] Skipping profile refresh - registration cooldown active');
          return;
        }
        console.log('[Auth] User returned to tab, refreshing profile...');
        await refreshCurrentUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser?.id]);

  // Auth actions
  const loginWithMagicLink = useCallback(async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('[MagicLink] Sending native Supabase magic link to:', email);

      // Use Supabase's native magic link - uses custom email template from dashboard
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error('[MagicLink] Error from Supabase:', error);
        throw error;
      }

      console.log('[MagicLink] Native magic link sent successfully');
      return {
        success: true,
        message: 'Check your email for the login link!',
      };
    } catch (error) {
      console.error('[MagicLink] Failed to send magic link:', error);

      // Provide helpful error messages
      let message = 'Failed to send magic link';
      if (error instanceof Error) {
        message = error.message;

        // Add helpful context for common errors
        if (message.includes('rate limit') || message.includes('Too many requests')) {
          message = 'Too many requests. Please wait a few minutes and try again.';
        } else if (message.includes('invalid email')) {
          message = 'Please enter a valid email address.';
        } else if (message.includes('not recognized') || message.includes('Email not confirmed')) {
          message = 'Email address not found. Please check your email or sign up.';
        }
      }

      return {
        success: false,
        message,
      };
    }
  }, []);

  const login = useCallback(async (credentials: { email: string; password: string }): Promise<boolean> => {
    try {
      console.log('AuthProvider: Attempting login for:', credentials.email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      if (error) {
        const readable =
          (error as any)?.status === 400
            ? 'Invalid email or password (or email not confirmed).'
            : error.message || 'Login failed';

        console.error('Auth error (login):', { status: (error as any)?.status, message: error.message });
        console.error('AuthProvider: Login error:', error);
        throw error;
      }
      if (data.user && data.session) {
        console.log('AuthProvider: Login successful');
        setAccessToken(data.session.access_token);
        await handleSession(data.session);
        // Set GA4 user ID
        setUserId(data.user.id);
        return true;
      }
      return false;
    } catch (error) {
      console.error('AuthProvider: Login failed:', error);
      throw error;
    }
  }, []);

  /** register: create auth user; DB trigger creates profile row. Extras are saved post-confirm/onboarding. */
  const register = async (
    email: string,
    password: string,
    username: string,
    extras: RegisterExtras
  ): Promise<{ success: boolean; needsEmailConfirmation: boolean }> => {
    try {
      console.log('AuthProvider: Attempting registration for:', email);

      // 1) Create user with auto-confirm using edge function
      // This creates the user with email_confirmed=true AND triggers verification email
      const { data: createData, error: createError } = await supabase.functions.invoke('auto-confirm-email', {
        body: {
          email,
          password,
          username: username || email.split('@')[0],
          fullName: extras.fullName,
          gender: extras.gender,
          dateOfBirth: extras.dateOfBirth,
          phone: extras.phone,
        },
      });

      if (createError) {
        console.error('AuthProvider: Registration error:', createError);
        console.error('AuthProvider: Error details:', JSON.stringify(createError, null, 2));
        console.error('AuthProvider: Response data:', createData);

        // Extract meaningful error message from the response
        const errorMessage = createData?.error || createError.message || 'Registration failed';
        throw new Error(errorMessage);
      }

      if (!createData?.user?.id) {
        throw new Error('No user returned from registration');
      }

      console.log('AuthProvider: User created with auto-confirmed email:', createData.user.id);

      // 2) Start 1-hour preview window for interaction gating
      setPreviewForOneHour();

      // 3) Log in immediately to create session
      console.log('AuthProvider: Creating session after registration');
      const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInErr) {
        console.error('AuthProvider: Could not create session:', signInErr);
        throw signInErr;
      }

      if (signInData.session) {
        console.log('AuthProvider: Session created successfully');
        setAccessToken(signInData.session.access_token);
        await handleSession(signInData.session);
        registrationCooldownRef.current = Date.now() + 10000;
        console.log('AuthProvider: Registration cooldown set for 10 seconds');
      }

      console.log('AuthProvider: Registration successful - user is fully logged in');
      return { success: true, needsEmailConfirmation: false };
    } catch (error) {
      console.error('AuthProvider: Registration failed:', error);
      throw error;
    }
  };

  const logout = useCallback(async (): Promise<void> => {
    try {
      console.log('AuthProvider: Logging out');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('AuthProvider: Logout error (continuing with local cleanup):', error);
      }
    } catch (error) {
      console.warn('AuthProvider: Logout failed on server (continuing with local cleanup):', error);
    } finally {
      // Always clear local state regardless of server response
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
      setCompanionRequests([]);
      setFavorites([]);
      setBlockedUsers([]);
      setIncomingTribeRequests([]);
      setOutgoingTribeRequests([]);
      setMyTribeMembers([]);
      setAllTribeConnections([]);
      endPreviewNow();
      // Clear GA4 user ID
      clearUserId();
      console.log('AuthProvider: Logout successful');
    }
  }, []);

  const updateUser = useCallback(async (userData: User): Promise<boolean> => {
    try {
      console.log('AuthProvider: Updating user profile');

      // Build update object with only provided fields
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (userData.username !== undefined) updateData.username = userData.username;
      if (userData.fullName !== undefined) updateData.full_name = userData.fullName;
      if (userData.bio !== undefined) updateData.bio = userData.bio;
      if (userData.interests !== undefined) updateData.interests = userData.interests;
      if (userData.location !== undefined) updateData.location = userData.location;
      if (userData.latitude !== undefined) updateData.latitude = userData.latitude;
      if (userData.longitude !== undefined) updateData.longitude = userData.longitude;
      if (userData.gender !== undefined) updateData.gender = userData.gender;
      if (userData.dateOfBirth !== undefined) updateData.date_of_birth = userData.dateOfBirth;
      if (userData.phone !== undefined) updateData.mobile = userData.phone;
      if (userData.avatarUrl !== undefined) updateData.avatar_url = userData.avatarUrl;
      if (userData.profileVisibility !== undefined) updateData.profile_visibility = userData.profileVisibility;
      if (userData.profileStatus !== undefined) updateData.profile_status = userData.profileStatus;
      if (userData.showInTribeCarousel !== undefined) updateData.show_in_tribe_carousel = userData.showInTribeCarousel;

      // Special handling if backend code passes snake_case directly OR uses profileImage alias
      if ((userData as any).avatar_url !== undefined) updateData.avatar_url = (userData as any).avatar_url;
      if ((userData as any).profile_status !== undefined) updateData.profile_status = (userData as any).profile_status;
      if ((userData as any).profileImage !== undefined) updateData.avatar_url = (userData as any).profileImage;

      console.log('[AuthProvider] updateUser - About to update DB with keys:', Object.keys(updateData));

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', (userData as any).id || currentUser?.id);

      if (error) {
        console.error('[AuthProvider] updateUser - Database error:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        return false;
      }

      console.log('[AuthProvider] updateUser - Database update successful');

      // Refresh current user to get updated data
      await refreshCurrentUser();
      console.log('AuthProvider: Profile updated successfully');
      return true;
    } catch (error) {
      console.error('AuthProvider: Update user failed:', error);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const refreshCurrentUser = useCallback(async (): Promise<boolean> => {
    try {
      if (!currentUser) return false;
      console.log('AuthProvider: Refreshing current user');
      const user = await loadUserProfile(currentUser.id);
      return !!user;
    } catch (error) {
      console.error('AuthProvider: Error refreshing user:', error);
      return false;
    }
  }, [currentUser]);

  const setAdminUser = (user: User) => {
    console.log('AuthProvider: Setting admin user');
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  // Public helpers exposed to the UI
  const endPreview = () => endPreviewNow();

  const resendVerification = async () => {
    const { data } = await supabase.auth.getUser();
    const email = data.user?.email;
    if (!email) return;
    await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
  };

  const requestAndStoreCurrentLocation = useCallback(async (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser');
        resolve(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (!currentUser) {
            resolve(false);
            return;
          }

          try {
            const { latitude, longitude } = position.coords;

            // Step 1: Save coordinates immediately with default location name
            // This ensures location-based features work even if reverse geocoding fails
            // IMPORTANT: Only send the fields we're updating to avoid permission errors
            const immediateUpdate = {
              id: currentUser.id,
              latitude,
              longitude,
              location: 'My area',
            };

            const success = await updateUser(immediateUpdate as User);
            if (!success) {
              console.error('Failed to save coordinates');
              resolve(false);
              return;
            }

            // Step 2: Try to get actual address from Google Maps API (non-blocking)
            // If this fails, user still has working location with "My area"
            try {
              const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
              );
              const data = await response.json();

              if (data.results && data.results.length > 0) {
                const locationName = data.results[0].formatted_address;
                // Update location name without blocking the success response
                // IMPORTANT: Only send the fields we're updating
                await updateUser({
                  id: currentUser.id,
                  latitude,
                  longitude,
                  location: locationName,
                } as User);
              }
            } catch (geocodeError) {
              // Reverse geocoding failed, but coordinates are already saved
              console.warn('Reverse geocoding failed, using default location:', geocodeError);
            }

            // Always resolve true if coordinates were saved successfully
            resolve(true);
          } catch (error) {
            console.error('Error storing location:', error);
            resolve(false);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          resolve(false);
        },
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 300000 }
      );
    });
  }, [currentUser, updateUser]);

  // Tribe Connections actions
  const sendTribeConnectionRequest = useCallback(
    async (addresseeId: string, message?: string) => {
      const connection = await sendConnectionRequest(addresseeId, message);
      await refreshTribeData();
      return connection;
    },
    [refreshTribeData]
  );

  const acceptTribeConnection = useCallback(
    async (connectionId: string) => {
      const connection = await acceptConnection(connectionId);
      await refreshTribeData();
      return connection;
    },
    [refreshTribeData]
  );

  const rejectTribeConnection = useCallback(
    async (connectionId: string) => {
      const connection = await rejectConnection(connectionId);
      await refreshTribeData();
      return connection;
    },
    [refreshTribeData]
  );

  const withdrawTribeConnectionRequest = useCallback(
    async (connectionId: string) => {
      const connection = await withdrawRequest(connectionId);
      await refreshTribeData();
      return connection;
    },
    [refreshTribeData]
  );

  // Synchronous getters should also be stable if components put them in deps
  const getIncomingTribeRequests = useCallback(
    () => incomingTribeRequests,
    [incomingTribeRequests]
  );
  const getOutgoingTribeRequests = useCallback(
    () => outgoingTribeRequests,
    [outgoingTribeRequests]
  );
  const getMyTribeMembers = useCallback(
    () => myTribeMembers,
    [myTribeMembers]
  );

  const getTribeConnectionStatus = useCallback(
    (otherUserId: string): ConnectionStatus | 'none' => {
      if (!currentUser?.id) return 'none';
      const edge = allTribeConnections.find(
        (c) =>
          (c.requester_id === currentUser.id &&
            c.addressee_id === otherUserId) ||
          (c.requester_id === otherUserId &&
            c.addressee_id === currentUser.id)
      );
      return edge ? (edge.status as ConnectionStatus) : 'none';
    },
    [allTribeConnections, currentUser?.id]
  );

  // Messaging
  const sendMessage = useCallback(async (
    receiverId: string,
    content: string
  ): Promise<Message | null> => {
    try {
      if (!currentUser) return null;

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: currentUser.id,
          receiver_id: receiverId,
          content,
          read: false,
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        return null;
      }

      return {
        id: data.id,
        senderId: data.sender_id,
        receiverId: data.receiver_id,
        content: data.content,
        read: data.read,
        createdAt: data.created_at,
      };
    } catch (error) {
      console.error('Error in sendMessage:', error);
      return null;
    }
  }, [currentUser]);

  const getMessagesWith = useCallback(async (userId: string): Promise<Message[]> => {
    try {
      if (!currentUser) return [];
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(
          `and(sender_id.eq.${currentUser.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${currentUser.id})`
        )
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        return [];
      }

      return data.map((msg) => ({
        id: msg.id,
        senderId: msg.sender_id,
        receiverId: msg.receiver_id,
        content: msg.content,
        read: msg.read,
        createdAt: msg.created_at,
      }));
    } catch (error) {
      console.error('Error in getMessagesWith:', error);
      return [];
    }
  }, [currentUser]);

  const getConversations = useCallback(async (): Promise<Conversation[]> => {
    try {
      if (!currentUser) return [];

      // Fetch 1-on-1 conversations
      const { data, error } = await supabase
        .from('user_conversations')
        .select('*')
        .eq('user_id', currentUser.id);

      if (error) {
        console.error('Error fetching conversations:', error);
        return [];
      }

      const conversations: Conversation[] = [];

      // Process 1-on-1 conversations (bulk fetch to avoid N+1 query)
      if (data && data.length > 0) {
        // Collect all unique user IDs
        const userIds = data.map(conv => conv.other_user_id);

        // Bulk fetch all user profiles in a single query
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', userIds);

        if (!usersError && usersData) {
          // Create a map for quick lookup
          const usersMap = new Map(usersData.map(u => [u.id, u]));

          // Process each conversation with the fetched user data
          for (const conv of data) {
            const userData = usersMap.get(conv.other_user_id);
            if (!userData) continue;

            const user: User = {
              id: userData.id,
              username: userData.username || '',
              email: userData.email || '',
              fullName: userData.full_name,
              bio: userData.bio,
              interests: userData.interests || [],
              location: userData.location,
              latitude: userData.latitude,
              longitude: userData.longitude,
              gender: userData.gender,
              dateOfBirth: userData.date_of_birth,
              isVerified: userData.is_verified || false,
              verificationStatus: userData.verification_status || 'unverified',
              subscriptionTier: userData.subscription_tier || 'free',
              phone: userData.mobile,
              avatarUrl: userData.avatar_url,
              rating: userData.rating,
              ratingCount: userData.rating_count,
              profileVisibility: userData.profile_visibility || 'public',
              createdAt: userData.created_at,
            };

            const lastMessage: Message = {
              id: conv.last_message_id || '',
              senderId: conv.last_message_sender_id || '',
              receiverId: currentUser.id,
              content: conv.last_message_content || '',
              read: true,
              createdAt: conv.last_message_time || '',
            };

            conversations.push({
              user,
              lastMessage,
              unreadCount: Number(conv.unread_count) || 0,
            });
          }
        }
      }

      // Also fetch vybe threads (group chats) where user is a member
      try {
        console.log('🔍 Fetching vybe threads for user:', currentUser.id);

        // First, get unread counts for all threads
        const { data: unreadCounts, error: unreadError } = await supabase
          .rpc('get_thread_unread_counts', { user_id_input: currentUser.id });

        if (unreadError) {
          console.error('❌ Error fetching unread counts:', unreadError);
        }

        // Create a map for quick lookup
        const unreadMap = new Map<string, number>();
        if (unreadCounts) {
          unreadCounts.forEach((item: any) => {
            unreadMap.set(item.thread_id, Number(item.unread_count) || 0);
          });
        }
        console.log('📊 Unread counts map:', unreadMap);

        // Then, get all thread IDs where user is a participant
        const { data: participantData, error: participantError } = await supabase
          .from('thread_participants')
          .select('thread_id')
          .eq('user_id', currentUser.id);

        console.log('📋 Participant data:', participantData, 'Error:', participantError);

        if (participantError) {
          console.error('❌ Error fetching thread participants:', participantError);
        }

        if (participantData && participantData.length > 0) {
          const threadIds = participantData.map(p => p.thread_id);
          console.log('🔗 Thread IDs:', threadIds);

          // Get thread details with vybe info
          const { data: threadsData, error: threadsError } = await supabase
            .from('vybe_threads')
            .select('id, vybe_id')
            .in('id', threadIds);

          console.log('📊 Threads data:', threadsData, 'Error:', threadsError);

          if (threadsError) {
            console.error('❌ Error fetching threads:', threadsError);
          }

          if (threadsData && threadsData.length > 0) {
            const vybeIds = threadsData.map(t => t.vybe_id);
            console.log('🎯 Vybe IDs:', vybeIds);

            // Get event details
            const { data: eventsData, error: eventsError } = await supabase
              .from('events')
              .select('id, title, date, location, status, is_bot_created')
              .in('id', vybeIds);

            console.log('🎉 Events data:', eventsData, 'Error:', eventsError);

            if (eventsError) {
              console.error('❌ Error fetching events:', eventsError);
            }

            // Create a map for quick lookup
            const eventsMap = new Map(eventsData?.map(e => [e.id, e]) || []);

            // Process each thread
            for (const thread of threadsData) {
              const event = eventsMap.get(thread.vybe_id);
              console.log('🔄 Processing thread:', thread.id, 'Event:', event);
              if (!event) continue;

            // Get member count
            const { count: memberCount } = await supabase
              .from('thread_participants')
              .select('*', { count: 'exact', head: true })
              .eq('thread_id', thread.id);

            // Get last message
            const { data: lastMsg } = await supabase
              .from('thread_messages')
              .select('*')
              .eq('thread_id', thread.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            // Create a pseudo-user object for the Vybe thread
            const vybeUser: User = {
              id: thread.id,
              username: event.title,
              email: '',
              fullName: event.title,
              interests: [],
              isVerified: false,
              verificationStatus: 'unverified',
              subscriptionTier: 'free',
              createdAt: '',
              vybeThreadMetadata: {
                eventId: event.id,
                eventDate: event.date,
                eventLocation: event.location,
                eventStatus: event.status,
                memberCount: memberCount || 0,
                isVybeThread: true,
                isBotCreated: event.is_bot_created || false,
              },
            } as any;

            const lastMessage: Message = lastMsg ? {
              id: lastMsg.id,
              senderId: lastMsg.sender_id || 'system',
              receiverId: currentUser.id,
              content: lastMsg.content || '',
              read: true,
              createdAt: lastMsg.created_at || '',
            } : {
              id: '',
              senderId: '',
              receiverId: currentUser.id,
              content: 'No messages yet',
              read: true,
              createdAt: '',
            };

              const threadUnreadCount = unreadMap.get(thread.id) || 0;
              console.log('✅ Adding vybe thread conversation:', vybeUser.username, 'Unread:', threadUnreadCount);
              conversations.push({
                user: vybeUser,
                lastMessage,
                unreadCount: threadUnreadCount,
              });
            }
          }
        } else {
          console.log('⚠️ No participant data or empty array');
        }
      } catch (error) {
        console.error('❌ Error fetching vybe threads:', error);
      }

      console.log('📦 Total conversations:', conversations.length);

      // Sort by last message time
      conversations.sort((a, b) => {
        const timeA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
        const timeB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
        return timeB - timeA;
      });

      console.log('✅ Returning conversations:', conversations);
      return conversations;
    } catch (error) {
      console.error('Error in getConversations:', error);
      return [];
    }
  }, [currentUser]);

  const markAllMessagesAsRead = useCallback(async (senderId: string): Promise<boolean> => {
    try {
      if (!currentUser) return false;
      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('sender_id', senderId)
        .eq('receiver_id', currentUser.id)
        .eq('read', false);
      if (error) {
        console.error('Error marking messages as read:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error in markAllMessagesAsRead:', error);
      return false;
    }
  }, [currentUser]);

  // Companion requests (mocked)
  const sendCompanionRequest = async (
    receiverId: string
  ): Promise<CompanionRequest | null> => {
    try {
      if (!currentUser) return null;
      const existingRequest = companionRequests.find(
        (req) => req.senderId === currentUser.id && req.receiverId === receiverId
      );
      if (existingRequest) throw new Error('Request already sent');

      const newRequest: CompanionRequest = {
        id: Math.random().toString(36).substring(2, 9),
        senderId: currentUser.id,
        receiverId,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      setCompanionRequests((prev) => [...prev, newRequest]);
      return newRequest;
    } catch (error) {
      console.error('Error sending companion request:', error);
      throw error;
    }
  };

  const respondToCompanionRequest = async (
    requestId: string,
    response: 'accepted' | 'rejected'
  ): Promise<boolean> => {
    try {
      setCompanionRequests((prev) =>
        prev.map((req) => (req.id === requestId ? { ...req, status: response } : req))
      );

      // add to favorites on accept (demo)
      if (response === 'accepted') {
        const request = companionRequests.find((req) => req.id === requestId);
        if (request && currentUser) {
          const { data: userData, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', request.senderId)
            .single();

          if (!error && userData) {
            const user: User = {
              id: userData.id,
              username: userData.username || '',
              email: userData.email || '',
              fullName: userData.full_name,
              bio: userData.bio,
              interests: userData.interests || [],
              location: userData.location,
              latitude: userData.latitude,
              longitude: userData.longitude,
              gender: userData.gender,
              dateOfBirth: userData.date_of_birth,
              isVerified: userData.is_verified || false,
              verificationStatus: userData.verification_status || 'unverified',
              subscriptionTier: userData.subscription_tier || 'free',
              phone: userData.mobile,
              avatarUrl: userData.avatar_url,
              rating: userData.rating,
              ratingCount: userData.rating_count,
              profileVisibility: userData.profile_visibility || 'public',
              createdAt: userData.created_at,
            };
            setFavorites((prev) => [...prev, user]);
          }
        }
      }
      return true;
    } catch (error) {
      console.error('Error responding to companion request:', error);
      return false;
    }
  };

  const getCompanionRequests = (): CompanionRequest[] => companionRequests;
  const isCompanion = (userId: string): boolean =>
    favorites.some((fav) => fav.id === userId);

  // Favorites
  const addToFavorites = async (userId: string): Promise<boolean> => {
    try {
      if (!currentUser) return false;
      const { data: userData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !userData) {
        console.error('Error loading user for favorites:', error);
        return false;
      }
      const user: User = {
        id: userData.id,
        username: userData.username || '',
        email: userData.email || '',
        fullName: userData.full_name,
        bio: userData.bio,
        interests: userData.interests || [],
        location: userData.location,
        latitude: userData.latitude,
        longitude: userData.longitude,
        gender: userData.gender,
        dateOfBirth: userData.date_of_birth,
        isVerified: userData.is_verified || false,
        verificationStatus: userData.verification_status || 'unverified',
        subscriptionTier: userData.subscription_tier || 'free',
        phone: userData.mobile,
        avatarUrl: userData.avatar_url,
        rating: userData.rating,
        ratingCount: userData.rating_count,
        profileVisibility: userData.profile_visibility || 'public',
        createdAt: userData.created_at,
      };
      setFavorites((prev) => [...prev, user]);
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return false;
    }
  };

  const removeFromFavorites = async (userId: string): Promise<boolean> => {
    try {
      setFavorites((prev) => prev.filter((fav) => fav.id !== userId));
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return false;
    }
  };

  const getFavorites = (): User[] => favorites;
  const hasAddedToFavorites = (userId: string): boolean =>
    favorites.some((fav) => fav.id === userId);
  const isMutualFavorite = (userId: string): boolean =>
    favorites.some((fav) => fav.id === userId);

  // Subscription / verification helpers
  const isVerified = useCallback((): boolean => {
    // Veriff verification is now optional and doesn't block app usage
    // All authenticated users can use the app freely
    return true;
  }, []);

  const isVerificationPending = useCallback((): boolean =>
    currentUser?.verificationStatus === 'pending',
  [currentUser?.verificationStatus]);

  const hasVeriffBadge = useCallback((): boolean => {
    // Check if user has completed Veriff ID verification (approved status)
    return currentUser?.isVerified === true &&
           currentUser?.verificationStatus === 'verified';
  }, [currentUser?.isVerified, currentUser?.verificationStatus]);

  const getSubscriptionDetails = useCallback(() => {
    if (!currentUser) return { planName: 'Free', status: 'inactive' };

    const tier = currentUser.subscriptionTier || 'free';
    const isCancelled = currentUser.subscriptionCancelled || false;
    const expiresAt = currentUser.subscriptionExpiresAt;

    // Only 'free' and 'subscribed' are valid tiers
    let planName = 'Free';
    let status: 'active' | 'inactive' | 'cancelling' = 'inactive';

    if (tier === 'subscribed') {
      planName = 'Subscribed';
      if (isCancelled && expiresAt) {
        // Subscription is cancelled but still active until expiration
        status = 'cancelling';
      } else {
        status = 'active';
      }
    }

    return {
      planName,
      status,
      nextBillingDate: expiresAt,
    };
  }, [currentUser?.subscriptionTier, currentUser?.subscriptionCancelled, currentUser?.subscriptionExpiresAt]);

  const cancelSubscription = async (
    userId: string,
    immediate: boolean
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId, cancelImmediately: immediate }),
        }
      );
      if (!response.ok) throw new Error('Failed to cancel subscription');
      await refreshCurrentUser();
      return true;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return false;
    }
  };

  // Blocking
  const blockUser = async (userId: string): Promise<boolean> => {
    try {
      setBlockedUsers((prev) => [...prev, userId]);
      return true;
    } catch (error) {
      console.error('Error blocking user:', error);
      return false;
    }
  };
  const unblockUser = async (userId: string): Promise<boolean> => {
    try {
      setBlockedUsers((prev) => prev.filter((id) => id !== userId));
      return true;
    } catch (error) {
      console.error('Error unblocking user:', error);
      return false;
    }
  };
  const isUserBlocked = useCallback(
    (userId: string) => blockedUsers.includes(userId),
    [blockedUsers]
  );

  const checkIfCanConnect = async (
    userId: string,
    currentUserId: string
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('tribe_connections')
        .select('id')
        .eq('status', 'accepted')
        .or(
          `and(requester_id.eq.${currentUserId},addressee_id.eq.${userId}),and(requester_id.eq.${userId},addressee_id.eq.${currentUserId})`
        )
        .limit(1);
      if (error) {
        console.error('Error checking if users can connect:', error);
        return false;
      }
      return (data ?? []).length > 0;
    } catch (error) {
      console.error('Error in checkIfCanConnect:', error);
      return false;
    }
  };

  // Finally, memoize the context value so function identities stay stable
  const value = useMemo<AuthContextType>(
    () => ({
      currentUser,
      isAuthenticated,
      hasHydrated,
      sessionChecked,
      isLoading,
      accessToken,

      // NEW preview + verify helpers
      previewActive,
      previewSecondsLeft,
      endPreview,
      resendVerification,

      login,
      loginWithMagicLink,
      register,
      logout,
      updateUser,
      refreshCurrentUser,
      setAdminUser,
      requestAndStoreCurrentLocation,
      sendMessage,
      getMessagesWith,
      getConversations,
      markAllMessagesAsRead,

      // Tribe
      sendTribeConnectionRequest,
      acceptTribeConnection,
      rejectTribeConnection,
      withdrawTribeConnectionRequest,
      getIncomingTribeRequests,
      getOutgoingTribeRequests,
      getMyTribeMembers,
      getTribeConnectionStatus,
      refreshTribeData,

      // Blocking
      blockUser,
      unblockUser,
      isUserBlocked,
      checkIfCanConnect,

      // Companion
      sendCompanionRequest,
      respondToCompanionRequest,
      getCompanionRequests,
      isCompanion,

      // Favorites
      addToFavorites,
      removeFromFavorites,
      getFavorites,
      hasAddedToFavorites,
      isMutualFavorite,

      // Subscriptions
      isVerified,
      isVerificationPending,
      hasVeriffBadge,
      getSubscriptionDetails,
      cancelSubscription,

      // VIP Launch Access
      vipActive: isVipActive(currentUser),
      vipEndsAt: currentUser?.trialEndsAt ?? null,
    }),
    [
      currentUser,
      isAuthenticated,
      hasHydrated,
      sessionChecked,
      isLoading,
      accessToken,

      previewActive,
      previewSecondsLeft,

      login,
      register,
      logout,
      updateUser,
      refreshCurrentUser,
      setAdminUser,
      requestAndStoreCurrentLocation,
      sendMessage,
      getMessagesWith,
      getConversations,
      markAllMessagesAsRead,
      sendTribeConnectionRequest,
      acceptTribeConnection,
      rejectTribeConnection,
      withdrawTribeConnectionRequest,
      getIncomingTribeRequests,
      getOutgoingTribeRequests,
      getMyTribeMembers,
      getTribeConnectionStatus,
      refreshTribeData,
      blockUser,
      unblockUser,
      isUserBlocked,
      checkIfCanConnect,
      sendCompanionRequest,
      respondToCompanionRequest,
      getCompanionRequests,
      isCompanion,
      addToFavorites,
      removeFromFavorites,
      getFavorites,
      hasAddedToFavorites,
      isMutualFavorite,
      isVerified,
      isVerificationPending,
      hasVeriffBadge,
      getSubscriptionDetails,
      cancelSubscription,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
